import cx from 'classnames';
import { useState } from 'react';

import { Button, Icon } from '@sb/design-system';
import { wait } from '@sb/utilities';
import { RELOAD_GUARD_KEY, useSoftwareBuildData } from '@sbrc/hooks';

const PREFETCH_TIMEOUT_MS = 5_000;

export default function VersionMismatchBanner() {
  const { buildMismatchDetected } = useSoftwareBuildData();
  const [isReloading, setIsReloading] = useState(false);

  if (!buildMismatchDetected) {
    return null;
  }

  return (
    <div
      className={cx(
        'tw-fixed',
        'tw-top-0',
        'tw-left-0',
        'tw-right-0',
        'tw-z-50',
        'tw-flex',
        'tw-items-center',
        'tw-justify-center',
        'tw-gap-12',
        'tw-bg-yellow-60',
        'tw-px-16',
        'tw-py-8',
        'tw-text-black',
      )}
    >
      <Icon kind="exclamationTriangle" />
      <span>
        A new UI version is available but could not be loaded automatically.
      </span>
      <Button
        variant="Flat"
        size={32}
        disabled={isReloading}
        onClick={async () => {
          setIsReloading(true);
          sessionStorage.removeItem(RELOAD_GUARD_KEY);

          // Vite content-hashes /assets filenames, but BUILD_ID is sed-injected
          // post-hash (see apps/remote-control/server/Dockerfile), so backend-only
          // deploys produce identical filenames with different BUILD_ID bytes.
          // The 1-year /assets cache then pins the user to the old bundle.
          // Force-refetch every loaded /assets resource with `cache: 'reload'`
          // so the browser HTTP cache is repopulated with the new server content
          // before we navigate. Cap total wait so a slow/unreachable network
          // can't make the click look dead — navigation always happens.
          const assetUrls = performance
            .getEntriesByType('resource')
            .map((e) => e.name)
            .filter((url) =>
              url.startsWith(`${window.location.origin}/assets/`),
            );

          await Promise.race([
            Promise.all(
              assetUrls.map((url) =>
                fetch(url, { cache: 'reload' })
                  // fetch resolves on headers; consume the body so Firefox/Safari
                  // commit the full response to the HTTP cache before navigation
                  // aborts in-flight streams.
                  .then((r) => r.arrayBuffer())
                  .catch(() => {}),
              ),
            ),
            wait(PREFETCH_TIMEOUT_MS),
          ]);

          const url = new URL(window.location.href);
          url.searchParams.set('_cb', String(Date.now()));
          window.location.replace(url.toString());
        }}
      >
        {isReloading ? 'Reloading…' : 'Reload now'}
      </Button>
    </div>
  );
}
